import {
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import React from "react";
import { colors, fontFamily, fontWeight, margin, padding } from "theme/defaultStyle";
import SpecialRisksGridRow from "./SpecialRisksGridRow";
import { getBlankRow, SelectedSpecialRiskType } from "./SpecialRisks";

interface SpecialRisksGridProps {
  schema: string;
  hasError: boolean;
  disabled?: boolean;
  selectedSpecialRisks: SelectedSpecialRiskType[];
  setSelectedSpecialRisks: React.Dispatch<React.SetStateAction<SelectedSpecialRiskType[]>>;
  specialRiskOptions: SelectFieldOption[];
  updateFormValues: (risks: SelectedSpecialRiskType[]) => void;
  specialRiskItems?: any[];
}

const StyledTableContainer = styled(Paper)({
  overflowX: "auto",
  overflowY: "hidden",
  marginBottom: margin.xlarge1,
  "& .MuiOutlinedInput": {
    paddingTop: padding.xsmall2,
    paddingBottom: padding.xsmall2,
  },
  "& .MuiAutocomplete-endAdornment": {
    top: 0,
  },
  "& .headCell": {
    padding: `${padding.small2} ${padding.zero} ${padding.xsmall2} ${padding.small2}`,
  },
  "& .headCellCenter": {
    padding: `${padding.small2} ${padding.zero} ${padding.xsmall2} ${padding.zero}`,
  },
});

const StyledTableCell = styled(TableCell)({
  "&.headCell": {
  paddingLeft: padding.small1,
  fontFamily: `${fontFamily.inter} !important`,
  fontWeight: `${fontWeight.bold1} !important`,
  }
})

const SpecialRisksGrid = ({
  schema,
  hasError,
  disabled = false,
  specialRiskOptions,
  selectedSpecialRisks,
  setSelectedSpecialRisks,
  updateFormValues,
  specialRiskItems,
}: SpecialRisksGridProps) => {

  const handleOnRemove = (id: any) => {
    setSelectedSpecialRisks((prevSelectedSpecialRisks: SelectedSpecialRiskType[]) => {
      const hasBlankRow = prevSelectedSpecialRisks.some(
        (risk: any) => !risk.selectedSpecialRisk
      );

      const filteredRisks = prevSelectedSpecialRisks.filter(
        (risk: any) => risk.id !== id
      );

      const updatedRisks = hasBlankRow ? filteredRisks : [...filteredRisks, getBlankRow()];   
      updateFormValues(updatedRisks);

      return updatedRisks;
    });
  };

  const handleSpecialRiskChange = (id: string, value: any) => {
    setSelectedSpecialRisks((prevSelectedSpecialRisks: SelectedSpecialRiskType[]) => {
      const index = prevSelectedSpecialRisks.findIndex((d: SelectedSpecialRiskType) => d.id === id);
      if (index === -1) return prevSelectedSpecialRisks;

      const updatedState = [...prevSelectedSpecialRisks];

      updatedState[index] = {
        ...updatedState[index],
        configCode: value?.value,
        selectedSpecialRisk: value?.text,
        integrationKey: value?.integrationKey,
        uniqueIdentifier: value?.integrationKey,
      };

      if (updatedState.length - 1 === index) {
        updatedState.push(getBlankRow());
      }

      updateFormValues(updatedState);

      return updatedState;
    });
  };

  const disableDelete = selectedSpecialRisks.length === 1;

  return (
    <>
      <>
        <StyledTableContainer
          elevation={0}
          style={{
            backgroundColor: disabled ? colors.grey03 : colors.white,
          }}
        >
          <Table size="small" className="endorsements-table">
            <TableHead style={{ height: 40, backgroundColor: colors.white }}>
              <TableRow>
                <StyledTableCell
                  classes={{ sizeSmall: "headCell" }}
                  style={{ minWidth: 340 }}
                >
                  Special Risks
                </StyledTableCell>

                <TableCell classes={{ sizeSmall: "headCell" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedSpecialRisks?.map((specialRisk: SelectedSpecialRiskType, index: number) => {
                // console.log("specialRisk:", specialRisk);
                // console.log("specialRiskItems:", specialRiskItems);

                if (specialRisk.configCode && !specialRisk.selectedSpecialRisk) {
                  specialRisk.selectedSpecialRisk = specialRiskItems?.find((ri) => ri.ConfigCode === specialRisk.configCode)?.ConfigValue || "";
                }
                // console.log("specialRisk -after:", specialRisk);
                return (
                  <SpecialRisksGridRow
                    key={specialRisk.id}
                    schema={schema}
                    hasError={hasError}
                    index={index}
                    specialRisk={specialRisk}
                    specialRiskOptions={specialRiskOptions}
                    handleOnRemove={handleOnRemove}
                    handleSpecialRiskChange={handleSpecialRiskChange}
                    disabled={disabled}
                    disableDelete={disableDelete}
                  />
                )
              }
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </>
    </>
  );
};

export default SpecialRisksGrid;
